import { useCallback, useEffect, useRef } from 'react';
import { overlay } from 'overlay-kit';
import { useGetMyVouchers } from '@/api/gen/vouchers/vouchers.gen';
import { useUser } from '@/lib/auth';
import { isPhoneTrialUpsellEligible, isPhoneVerificationPromptSuppressed, suppressPhoneVerificationPromptForToday } from '@/shared/features/phone-verification/utils';
import { markPromotionModalShown, wasPromotionModalShown } from '@/shared/features/promotion-modal-state';
import { PhoneVerificationBannerModal } from '../components/PhoneVerificationBannerModal';

/**
 * 휴대폰 인증 배너 모달을 관리하는 훅
 *
 * 다음 조건을 모두 만족할 때 모달을 표시합니다:
 * 1. 로그인한 사용자
 * 2. 휴대폰 인증을 하지 않은 사용자
 * 3. "오늘 하루동안 보지 않기" 쿠키가 설정되지 않은 경우
 * 4. 학생 계정이 아닌 경우
 * 5. KR 국가 사용자인 경우
 *
 * 가입 직후는 `SignUpAction` 이 인증 페이지로 직행시키므로 여기는 재유입만 맡는다.
 */
export const usePhoneVerificationBannerModal = ({
  enabled = true
}: {
  enabled?: boolean;
} = {}) => {
  const {
    user,
    isAuthenticated,
    isInitialized
  } = useUser();
  const {
    data: vouchersResponse,
    isLoading: isVouchersLoading
  } = useGetMyVouchers({
    usageStatus: 'UNUSED'
  }, {
    query: {
      enabled: enabled && isAuthenticated,
      throwOnError: false
    }
  });
  const hasOpenedRef = useRef(false);
  const openModal = useCallback(() => {
    requestAnimationFrame(() => {
      overlay.open(({
        isOpen,
        unmount
      }) => <PhoneVerificationBannerModal isOpen={isOpen} onClose={unmount} onDismiss={unmount} onDismissForToday={suppressPhoneVerificationPromptForToday} onOpen={markPromotionModalShown} />);
    });
  }, []);
  const shouldShowModal = useCallback(() => {
    if (!enabled) return false;
    if (!isInitialized) return false;
    if (!isAuthenticated || !user) return false;
    if (isVouchersLoading) return false;
    if (!isPhoneTrialUpsellEligible(vouchersResponse?.data.trialOffer)) return false;
    if (isPhoneVerificationPromptSuppressed(user.quizUserId)) return false;
    // 앱 전역 "페이지 로드당 프로모션 모달 1개" 게이트 — 인증 억제와 별개 축.
    if (wasPromotionModalShown()) return false;
    return true;
  }, [isInitialized, enabled, isAuthenticated, isVouchersLoading, user, vouchersResponse]);
  useEffect(() => {
    if (hasOpenedRef.current) return;
    if (shouldShowModal()) {
      hasOpenedRef.current = true;
      openModal();
    }
  }, [shouldShowModal, openModal]);
};